import axios from "axios";
import { useCallback, useState } from "react";
import { getOrigin } from "src/environment";
import { getDateTimeRecord, getUserId } from "src/utils";
import { Notification } from "src/utils/Notification";

const apiUrl = getOrigin() + "/api/v1/main.php";
// if (import.meta.env.VITE_APPLICATION_TYPE === "local") {
//   apiUrl = "https//dev.ljelogbooks.com//api/v1/main.php";
// }
/**
 * Custom hook to handle file uploads via an API.
 *
 * @returns An object containing the `upload` function and the `isUploading` state.
 */
export const useUploadFileApi = () => {
  const [isLoading, setIsLoading] = useState(false);
  /**
   * Uploads files to the API.
   *
   * @param request - The API request identifier.
   * @param files - An array of files to be uploaded.
   * @param body - (Optional) Additional data to include in the request body.
   * @param onSuccess - (Optional) Callback function to execute on successful upload.
   * @param fileIsSingle - (Optional) Whether the upload is for a single file. Defaults to `false`.
   */
  const upload = useCallback(
    async (
      request: string,
      files: File[],
      body?: Record<string, string>,
      onSuccess?: (data: any) => void,
      fileIsSingle = false,
    ) => {
      setIsLoading(true);

      try {
        let result: any = null;
        if (window?.api?.post) {
          result = await window.api.uploadFiles(request, files, body);
        } else {
          const formData = new FormData();
          formData.append("request", request);
          formData.append("_userid", getUserId());
          formData.append("_app", "true");
          formData.append("dto", JSON.stringify(getDateTimeRecord()));
          if (fileIsSingle && files.length === 1) {
            formData.append("file", files[0]);
          } else {
            files.forEach((file, i) => {
              formData.append(`file-${i}`, file);
            });
          }

          if (body) {
            Object.entries(body).forEach(([key, value]) => {
              formData.append(key, value);
            });
          }

          const res = await axios({
            url: apiUrl,
            method: "POST",
            data: formData,
            headers: {
              "Content-Type": "multipart/form-data; ",
            },
          });
          result = res.data;
        }

        if (result.error) {
          throw new Error(
            typeof result.error === "string"
              ? result.error
              : "Something went wrong.",
          );
        }

        if (result.message) Notification("Offline", result.message, "green");

        if (onSuccess) onSuccess(result);

        return result;
      } catch (error) {
        Notification("Error", String(error), "red");
        return undefined;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  return { upload, isUploading: isLoading };
};
